<template>
  <dropdown
    ref="dueDateDropdown"
    :class="{'has-calendar': showCalendar }"
    title="Due"
    placement="bottom"
    class="ifp-filter-due"
    @close="dueDateOpen = false"
  >
    <base-ifp-filter
      label="Due"
      clearable
      @click="dueDateOpen = !dueDateOpen"
      @clear-clicked="handleClearClick()"
    >
      <template #value-display>
        {{ selectedDueDateLabel }}
      </template>
    </base-ifp-filter>
    <!-- DUE DATE OPTIONS LIST -->
    <template #content>
      <div>
        <ul>
          <li
            v-for="dueType in filteredDueDateOptions"
            :key="dueType.id"
            :class="{'has-calendar': showCalendar }"
            @click="handleDueTypeClick(dueType.id)"
          >
            <span :class="{ 'selected': dueType.id === selectedDueType }">
              {{ dueType.name }}
            </span>
            <!-- DUE DATE PICKER FOR DATE RANGE -->
            <div v-if="isDueDateRangeOption(dueType.id)">
              <template v-if="$screen.isTabletOrDesktop($mq)">
                <due-date-range-picker
                  v-show="showCalendar"
                  v-model="calendarValue"
                  :format="dateFormat"
                  unlink-panels
                  @selected-range="handleSelectedRange"
                />
              </template>
            </div>
          </li>
        </ul>
      </div>
    </template>
  </dropdown>
</template>
<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import dayjs from '@/helpers/dayjs'

/* Mixins */

/* Components */

/* Helpers */

/* Data types */
import { IItemForSelection } from '@/api/types/response.types'
import { DueTypeValues } from '@/types'

/* API */

export default defineComponent({
  name: 'IfpFilterDue',
  props: {
    selectedDueType: {
      type: Number,
      default: null,
    },
    startDueDate: {
      type: String,
      default: '',
    },
    endDueDate: {
      type: String,
      default: '',
    },
    dueDateOptions: {
      type: Array as () => IItemForSelection[],
      required: true,
    },
  },

  emits: [
    'end-due-date-updated',
    'selected-due-type-updated',
    'start-due-date-updated',
  ],
  data() {
    return {
      dueDateOpen: false,
      calendarValue: [] as any[],
      dateFormat: 'MMMM DD, YYYY',
      dateRange: [] as any[],
      readyToSelectDueDateRange: false, // Clicking "In date range..." but haven't selected range yet...
    }
  },
  computed: {
    filteredDueDateOptions(): IItemForSelection[] {
      if (this.$screen.isMobile(this.$mq)) {
        return this.dueDateOptions.filter(d => !this.isDueDateRangeOption(d.id))
      }
      return this.dueDateOptions
    },
    isDueDateRangeOptionSelected(): boolean {
      return this.isDueDateRangeOption(this.selectedDueType)
    },
    showCalendar(): boolean {
      return this.isDueDateRangeOptionSelected || this.readyToSelectDueDateRange
    },
    selectedDueDateLabel(): string {
      if (
        this.isDueDateRangeOptionSelected &&
        this.startDueDate &&
        this.endDueDate
      ) {
        return this.formattedDateRange()
      } else {
        const selDueDate = this.dueDateOptions.find(
          dueDate => dueDate.id === this.selectedDueType,
        )
        return selDueDate ? selDueDate.name : ''
      }
    },
  },

  watch: {
    dueDateOpen: {
      handler(value) {
        if (!value) {
          // If "In date range..." selected but user opts out...
          this.readyToSelectDueDateRange = false
        }
      },
    },
    startDueDate: {
      immediate: true,
      handler(value) {
        this.calendarValue = [
          this.startDueDate ? dayjs(this.startDueDate) : dayjs(),
          this.endDueDate ? dayjs(this.endDueDate) : dayjs(),
        ]

        const dateStart = dayjs(this.calendarValue[0])
        const dateEnd = dayjs(this.calendarValue[1])

        this.dateRange.push(dateStart.format(this.dateFormat))
        this.dateRange.push(dateEnd.format(this.dateFormat))
      },
    },
  },

  methods: {
    isDueDateRangeOption(id: number): boolean {
      return id === DueTypeValues.DATE_RANGE
    },
    /**
     * Helper function that returns date to show on select with correct date format
     */
    formattedDateRange(): string {
      const dateRange = this.dateRange
      const startDateObj = dayjs(dateRange[0], this.dateFormat)
      const startDate = startDateObj.format(this.getFormattedDate(dateRange[0]))
      const endDateObj = dayjs(dateRange[1], this.dateFormat)
      const endDate = endDateObj.format(this.getFormattedDate(dateRange[1]))
      if (startDate !== endDate) {
        return `${startDate}-${endDate}`
      } else {
        return `${startDate}`
      }
    },
    getFormattedDate(value: string): string {
      const currentDate = dayjs(new Date())
      return dayjs(value).isSame(currentDate, 'year') ? 'MMM D' : 'MMM D, YYYY'
    },
    handleSelectedRange(newValue: any) {
      // Without this, this method is re-invoked when going from "In date range..."
      // to another due type.
      if (!this.showCalendar) {
        return
      }
      this.dateRange = [...newValue]

      const dateRange = newValue.map((value: any, index: number) => {
        return dayjs(newValue[index]).format('YYYY-MM-DD')
      })

      this.updateSelectedDuetype(DueTypeValues.DATE_RANGE)

      if (this.startDueDate !== dateRange[0]) {
        this.updateStartDueDate(dateRange[0])
      }
      if (this.endDueDate !== dateRange[1]) {
        this.updateEndDueDate(dateRange[1])
      }

      this.closeDueDateDropdown()
    },
    handleDueTypeClick(val: number) {
      if (this.isDueDateRangeOption(val)) {
        this.readyToSelectDueDateRange = true
      } else {
        this.updateSelectedDuetype(val)
        this.readyToSelectDueDateRange = false
        this.closeDueDateDropdown()
        this.dateRange = []
        this.updateStartDueDate(null)
        this.updateEndDueDate(null)
      }
    },
    closeDueDateDropdown() {
      if (this.$refs.dueDateDropdown) {
        // @ts-ignore
        this.$refs.dueDateDropdown.handleClose()
      }
    },
    updateSelectedDuetype(val: number) {
      this.$emit('selected-due-type-updated', val)
    },
    updateStartDueDate(val: any) {
      this.$emit('start-due-date-updated', val)
    },
    updateEndDueDate(val: any) {
      this.$emit('end-due-date-updated', val)
    },
    handleClearClick() {
      this.handleDueTypeClick(null)
    },
  },
})
</script>

<style lang="scss">
.ifp-filter-due {
  ul {
    /* Due date dropdown items */
    li {
      .due-date-range-picker__inputs {
        display: none;
      }

      &.has-calendar {
        max-width: 144px;
      }

      .due-date-range-picker {
        position: absolute;
        top: 0;
        right: 0;
        z-index: 9999;
        float: right;
        width: 538px;
        height: 248px;
        padding: 0;
        overflow: hidden;

        .el-date-range-picker {
          padding: 0 !important;
        }

        .el-picker-panel {
          top: 0 !important;
        }

        .el-date-range-picker__header {
          width: 230px;
        }
      }
    }
  }

  /* Expanded due date range picker with calendar */
  &.has-calendar {
    .dropdown__popper {
      width: 734px;
      max-width: none;
      height: 278px;
    }
  }
}
</style>
